import type { PostHog } from 'posthog-js-v2';
import { IInsights } from '../../Contracts/IInsights';
import { GlobalEventBus } from '../GlobalEventBus';
import { subscribeInsightsToEventBus } from './insightsEventSubscriptions';
import { buildPosthogV2Bootstrap, PosthogV2Config } from './posthogV2Options';
import { mapEventName, PosthogV2PageContext } from './posthogV2PageContext';
import { WIDGET_STATE_EVENTS } from '../../Events/Widget/State';

export type PosthogV2Client = Pick<
    PostHog,
    'init' | 'capture' | 'register' | 'register_once'
>;
export type PosthogV2Module = { default: PosthogV2Client };
export type PosthogV2Loader = () => Promise<PosthogV2Module>;

/**
 * The only reference to the `posthog-js-v2` package in the codebase. Vite code-splits it into
 * its own chunk, which is only fetched when this loader runs, i.e. when the V2 instance is
 * configured through environment variables.
 *
 * @returns The lazily loaded posthog-js 1.418.x module
 */
export const loadPosthogV2: PosthogV2Loader = () => import('posthog-js-v2');

type QueuedCall =
    | { kind: 'capture'; event: string; properties?: Record<string, unknown> }
    | { kind: 'identify'; supplierId: string };

export const MAX_QUEUED_CALLS = 200;

const PERSON_PROPERTY_KEYS = {
    SET: '$set',
    SET_ONCE: '$set_once',
} as const;

const isPropertyBag = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * Second, isolated PostHog instance running a newer posthog-js than the bundled one.
 * Subscribes to the event bus synchronously and queues every call until the lazily loaded
 * module has been initialised, then flushes the queue in order.
 *
 * Identity: the distinct id is bootstrapped from the session prefix at init and never changes.
 * The prefix is only known once the checkout session exists, so init also waits for it.
 * Users are never identified; the supplier id is attached to every event as the `vendorIdent`
 * super property instead.
 *
 * Temporary during the PostHog migration. See docs/posthogMigration.md for how this class
 * replaces the legacy PosthogAnalytics once the old instance is switched off.
 */
export class PosthogV2Analytics implements IInsights {
    public readonly ready: Promise<void>;
    private client: PosthogV2Client | null = null;
    private failed = false;
    private queue: QueuedCall[] = [];
    private readonly pageContext: PosthogV2PageContext;

    public constructor({
        logger,
        eventBus,
        config,
        distinctId,
        load = loadPosthogV2,
        pageContext = new PosthogV2PageContext(),
    }: {
        logger: ILogger;
        eventBus: typeof GlobalEventBus;
        config: PosthogV2Config;
        distinctId: Promise<string>;
        load?: PosthogV2Loader;
        pageContext?: PosthogV2PageContext;
    }) {
        this.pageContext = pageContext;
        // Registered before the insights subscriptions so the page is known when their events arrive.
        eventBus.listen(
            WIDGET_STATE_EVENTS.WIDGET_NAVIGATION_CHANGED,
            (location) => {
                this.pageContext.update(
                    location.widget.id,
                    location.widget.type,
                    location.name
                );
            }
        );
        subscribeInsightsToEventBus(eventBus, this);
        this.ready = Promise.all([load(), distinctId])
            .then(([{ default: client }, resolvedDistinctId]) => {
                const { projectId, ...options } = config;
                client.init(projectId, {
                    ...options,
                    bootstrap: buildPosthogV2Bootstrap(resolvedDistinctId),
                });
                this.client = client;
                this.flush();
            })
            .catch((error: unknown) => {
                this.failed = true;
                this.queue = [];
                logger.error(
                    error instanceof Error ? error : new Error(String(error)),
                    'PosthogV2Analytics failed to initialise'
                );
            });
    }

    /**
     * Captures an event under its PostHog name, attributed to the current virtual widget page.
     * Person properties (`$set` / `$set_once`) would be dropped because this
     * instance never creates person profiles, so they are registered as super properties first
     * and stripped from the event payload; the event and every later one then carry them.
     *
     * @param event - PostHog event name
     * @param properties - Event properties, optionally including `$set` / `$set_once`
     * @returns void
     */
    trackEvent = (
        event: string,
        properties?: Record<string, unknown>
    ): void => {
        if (this.failed) return;
        // Enriched at call time so the payload reflects the widget page at the moment of the event.
        const eventName = mapEventName(event);
        const enriched = this.pageContext.enrich(properties);
        if (!this.client) {
            this.enqueue({
                kind: 'capture',
                event: eventName,
                properties: enriched,
            });
            return;
        }
        this.capture(this.client, eventName, enriched);
    };

    /**
     * Attaches the supplier to all subsequent events as the `vendorIdent` super property.
     * Deliberately does not call `posthog.identify`: the V2 instance keeps the bootstrapped
     * anonymous distinct id for its whole lifetime, so the user id is ignored.
     *
     * @param _userId - Ignored; the distinct id is fixed at init from the session prefix
     * @param supplierId - The supplier the widget belongs to
     * @returns void
     */
    identify = (_userId: string, supplierId: string): void => {
        if (this.failed) return;
        if (!this.client) {
            this.enqueue({ kind: 'identify', supplierId });
            return;
        }
        this.client.register({ vendorIdent: supplierId });
    };

    private capture(
        client: PosthogV2Client,
        eventName: string,
        properties: Record<string, unknown>
    ): void {
        client.capture(
            eventName,
            this.registerPersonPropertiesAsSuperProperties(properties)
        );
    }

    private registerPersonPropertiesAsSuperProperties(
        properties?: Record<string, unknown>
    ): Record<string, unknown> | undefined {
        if (!properties || !this.client) {
            return properties;
        }
        const {
            [PERSON_PROPERTY_KEYS.SET]: set,
            [PERSON_PROPERTY_KEYS.SET_ONCE]: setOnce,
            ...eventProperties
        } = properties;
        if (isPropertyBag(setOnce)) {
            this.client.register_once(setOnce);
        }
        if (isPropertyBag(set)) {
            this.client.register(set);
        }
        return eventProperties;
    }

    private enqueue(call: QueuedCall): void {
        if (this.queue.length >= MAX_QUEUED_CALLS) {
            this.queue.shift();
        }
        this.queue.push(call);
    }

    private flush(): void {
        const pending = this.queue;
        this.queue = [];
        for (const call of pending) {
            if (call.kind === 'identify') {
                this.identify('', call.supplierId);
                continue;
            }
            if (this.client) {
                // already mapped and enriched when it was queued
                this.capture(this.client, call.event, call.properties ?? {});
            }
        }
    }
}
